"use client";

import { useCallback, useRef, useState } from "react";
import { UserAvatar } from "@workspace/ui/components/avatar";
import { Input } from "@workspace/ui/components/input";
import { X } from "lucide-react";
import { cn } from "@workspace/ui/lib/utils";
import { apiClient } from "@/lib/api-client";
import { UserHoverCard } from "./user-hover-card";
import useSWR from "swr";

interface UserSearchResult {
  id: string;
  username: string;
  avatarUrl: string | null;
  initials: string | null;
  avatarBgColor: string | null;
}

const userSearchFetcher = async (url: string) => {
  const result = await apiClient.safeRequest<{ results: UserSearchResult[] }>(
    url,
  );
  return result.ok ? result.data : { results: [] };
};

// ─── Single user chip (view mode) ────────────────────────────────────────────

interface UserChipViewProps {
  username: string;
  onRemove?: () => void;
}

export function UserChipView({ username, onRemove }: UserChipViewProps) {
  const chip = (
    <span
      className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full bg-muted text-sm font-medium"
    >
      <UserAvatar
        username={username}
        email=""
        size="sm"
        className="h-5 w-5 text-[10px]"
      />
      <span className="truncate max-w-[140px]">{username}</span>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 rounded-full hover:bg-foreground/10 p-0.5"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );

  if (onRemove) return chip;

  return (
    <UserHoverCard username={username}>
      {chip}
    </UserHoverCard>
  );
}

// ─── User typeahead input ────────────────────────────────────────────────────

interface UserTypeaheadProps {
  onSelect: (username: string) => void;
  exclude?: string[];
  placeholder?: string;
}

export function UserTypeahead({
  onSelect,
  exclude = [],
  placeholder = "Search users...",
}: UserTypeaheadProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data } = useSWR(
    query.length >= 1 ? `/api/users/search?q=${encodeURIComponent(query)}&limit=8` : null,
    userSearchFetcher,
    { dedupingInterval: 300 }
  );

  const results = (data?.results ?? []).filter(
    (u) => !exclude.includes(u.username)
  );

  const handleSelect = useCallback(
    (username: string) => {
      onSelect(username);
      setQuery("");
      setOpen(false);
    },
    [onSelect]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSelect(results[highlightIndex].username);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Reset the highlight whenever the result set changes size, adjusted DURING
  // RENDER rather than in an effect. An effect would commit one render with the
  // old index still pointing into the new list, so an Enter landing in that gap
  // would select the wrong user; React re-runs this component on the set below
  // before any handler can fire.
  const resultsLen = results.length;
  const [lastResultsLen, setLastResultsLen] = useState(resultsLen);
  if (resultsLen !== lastResultsLen) {
    setLastResultsLen(resultsLen);
    setHighlightIndex(0);
  }

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(e.target.value.length >= 1);
        }}
        onFocus={() => query.length >= 1 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="h-8 text-sm px-2"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
          {results.map((user, i) => (
            <button
              key={user.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(user.username)}
              className={cn(
                "flex items-center gap-2 w-full px-2 py-1.5 text-sm hover:bg-accent",
                i === highlightIndex && "bg-accent"
              )}
            >
              <UserAvatar
                username={user.username}
                email=""
                avatarUrl={user.avatarUrl ?? undefined}
                initials={user.initials ?? undefined}
                backgroundColor={user.avatarBgColor ?? undefined}
                size="sm"
                className="h-6 w-6 text-[11px]"
              />
              <span className="truncate">{user.username}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Single user field (view + edit) ─────────────────────────────────────────

interface SingleUserFieldProps {
  value?: string;
  editing?: boolean;
  onChange?: (username: string) => void;
}

export function SingleUserField({
  value,
  editing,
  onChange,
}: SingleUserFieldProps) {
  if (!editing) {
    return value ? (
      <UserChipView username={value} />
    ) : (
      <span className="text-sm text-muted-foreground italic">—</span>
    );
  }

  return (
    <div className="space-y-1">
      {value && (
        <UserChipView
          username={value}
          onRemove={() => onChange?.("")}
        />
      )}
      {!value && (
        <UserTypeahead
          onSelect={(u) => onChange?.(u)}
          exclude={value ? [value] : []}
        />
      )}
    </div>
  );
}

// ─── Multi-user field (view + edit) ──────────────────────────────────────────

interface MultiUserFieldProps {
  value?: string[];
  editing?: boolean;
  onChange?: (usernames: string[]) => void;
}

export function MultiUserField({
  value = [],
  editing,
  onChange,
}: MultiUserFieldProps) {
  if (!editing) {
    return value.length > 0 ? (
      <div className="flex flex-wrap gap-1">
        {value.map((u) => (
          <UserChipView key={u} username={u} />
        ))}
      </div>
    ) : (
      <span className="text-sm text-muted-foreground italic">—</span>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1">
        {value.map((u) => (
          <UserChipView
            key={u}
            username={u}
            onRemove={() => onChange?.(value.filter((v) => v !== u))}
          />
        ))}
      </div>
      <UserTypeahead
        onSelect={(u) => onChange?.([...value, u])}
        exclude={value}
      />
    </div>
  );
}
